import { useCallback, useState } from 'react';

import {
  getUnmetIdRequirements,
  hasUncheckedIdRequirementWaiver,
} from 'helpers/candidate_application/form-page-helpers';
import useFormSubmission from 'hooks/use-form-submission';
import usePageVisibilityRedirect from 'hooks/use-page-visibility-redirect';
import useResetSubsequentSections from 'hooks/use-reset-subsequent-sections';
import useVisibleSubsections from 'hooks/use-visible-subsections';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CustomToast } from 'components/Toast/CustomToast';

import { selectCandidateApplication } from 'api/candidate-application-slice';

import FormPageRenderer from '../../FormPages/FormPageRenderer';

const AdditionalDetails = ({
  candidate,
  brand,
  handlers,
  token,
  sectionKey,
  pageIndex,
  section,
  subsections,
  hasIndexPage,
  isSinglePage,
  fetched,
  check_values,
}) => {
  const { t: translate } = useTranslation();
  const { candidateFields } = useSelector(selectCandidateApplication);
  const visibleSubsections = useVisibleSubsections(subsections);
  const { saveFormValuesAndSubmit } = useFormSubmission();
  const { buildSystemValuesWithReset } = useResetSubsequentSections(sectionKey, check_values);
  const [customError, setCustomError] = useState('');

  const totalPages = (visibleSubsections || [])?.length || 0;
  const currentPage = parseInt(pageIndex) || 0;

  usePageVisibilityRedirect({
    currentPage,
    totalPages,
    visibleSubsections,
    preSubsectionFlows: [],
    hasIndexPage: false,
    sectionKey,
    token,
  });

  const handleFormSubmit = useCallback(
    async (formData, context, temp_Save = false, rejectedValuesFix = false) => {
      try {
        const hasValidationIssues = context?.hasValidationIssues || false;
        const currentPagePrefixes = context?.currentPagePrefixes || null;

        setCustomError('');

        const subsectionsToCheck =
          currentPage === totalPages - 1 ? visibleSubsections : context?.subsections;

        const unmetRequirements = getUnmetIdRequirements(
          subsectionsToCheck,
          candidateFields?.idRequirementsMet,
        );

        let hasCustomError = false;
        if (
          hasUncheckedIdRequirementWaiver({
            unmetRequirements,
            candidateFields,
            watchValues: formData,
            currentPagePrefixes,
          })
        ) {
          setCustomError(translate('candidate.sections.requiredDocumentsNotMet'));
          hasCustomError = true;
          return false;
        }

        let status = 'in-progress';
        if (currentPage === totalPages - 1 && !hasValidationIssues && !hasCustomError) {
          status = 'completed';
        } else if (hasValidationIssues) {
          status = 'in-progress';
        }

        const systemValues = buildSystemValuesWithReset('additional_details_status', status);

        const fixResult = await saveFormValuesAndSubmit(
          sectionKey,
          formData,
          token,
          systemValues,
          currentPagePrefixes,
          rejectedValuesFix,
        );

        if (currentPage === totalPages - 1 && !temp_Save) {
          toast.custom(
            t => (
              <CustomToast
                t={t}
                title={translate('candidate.sections.additionalDetailsAreSafe')}
                message={translate('candidate.sections.weveSavedYourAdditionalInformation')}
                type="success"
              />
            ),
            {
              duration: 5000,
            },
          );
        }
        return fixResult ?? true;
      } catch (error) {
        toast.custom(
          t => (
            <CustomToast
              t={t}
              title={translate('candidate.sections.failedToSaveAdditionalDetails')}
              message={error?.message || error || translate('candidate.sections.pleaseTryAgain')}
              type="error"
            />
          ),
          {
            duration: 5000,
          },
        );
        return false;
      }
    },
    [
      token,
      currentPage,
      totalPages,
      saveFormValuesAndSubmit,
      sectionKey,
      buildSystemValuesWithReset,
      candidateFields,
      translate,
      visibleSubsections,
    ],
  );

  return (
    <FormPageRenderer
      subsections={visibleSubsections}
      candidate={candidate}
      brand={brand}
      handlers={handlers}
      fetched={fetched}
      singlePage={isSinglePage}
      sectionKey={sectionKey}
      pageIndex={isSinglePage ? undefined : currentPage}
      globalPageIndex={currentPage}
      totalPages={totalPages}
      primaryActionLabel={translate('common.continue')}
      onFormSubmit={handleFormSubmit}
      customError={customError}
    />
  );
};

export default AdditionalDetails;
